import {
  addCurrentAuthHeader,
  type SessionService,
} from '@efdigital/ll-addon-auth';
import type { Apis } from '@efdigital/ll-addon-auth/types/apis';
import type { CoachingSession } from '@efdigital/ll-addon-data';
import type {
  CoacheeGetQuotaResponseBody,
  CoacheePostAttendanceMarkRequestBody,
  CoacheePostAttendanceMarkResponseBody,
  CoacheePostBookSessionRequestBody,
  CoacheePostBookSessionResponseBody,
  CoacheePostCancelSessionRequestBody,
  CoacheePostCancelSessionResponseBody,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';

export async function buildCoacheeGetQuotaRequest(
  apis: Apis,
  session: SessionService,
  coachingPlanId: string
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coachee/coaching-plans/${encodeURIComponent(coachingPlanId)}/quota`,
    init
  );
}

export async function parseCoacheeGetQuotaResponse(
  response: Response
): Promise<CoacheeGetQuotaResponseBody> {
  if (!response.ok) {
    throw new Error('Failed to fetch coaching quota');
  }

  const data = (await response.json()) as CoacheeGetQuotaResponseBody;
  return data;
}

export async function buildCoacheeBookSessionRequest(
  apis: Apis,
  session: SessionService,
  idempotencyKey: string,
  body: CoacheePostBookSessionRequestBody
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coachee/coaching-sessions`,
    init
  );
}

export async function parseCoacheeBookSessionResponse(
  response: Response
): Promise<CoacheePostBookSessionResponseBody> {
  if (!response.ok) {
    throw new Error('Failed to book session');
  }

  const data = (await response.json()) as CoacheePostBookSessionResponseBody;
  return data;
}

export async function buildCoacheeCancelSessionRequest(
  apis: Apis,
  session: SessionService,
  coachingSession: CoachingSession,
  body: CoacheePostCancelSessionRequestBody
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coachee/coaching-sessions/${encodeURIComponent(coachingSession.id!)}/cancel`,
    init
  );
}

export async function parseCoacheeCancelSessionResponse(
  response: Response
): Promise<CoacheePostCancelSessionResponseBody> {
  if (!response.ok) {
    throw new Error('Failed to cancel session');
  }

  const data = (await response.json()) as CoacheePostCancelSessionResponseBody;
  return data;
}

export async function buildCoacheeMarkAttendanceRequest(
  apis: Apis,
  session: SessionService,
  coachingSession: CoachingSession,
  body: CoacheePostAttendanceMarkRequestBody
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coachee/coaching-sessions/${encodeURIComponent(coachingSession.id!)}/attendance`,
    init
  );
}

export async function parseCoacheeMarkAttendanceResponse(
  response: Response
): Promise<CoacheePostAttendanceMarkResponseBody> {
  if (!response.ok) {
    throw new Error('Failed to mark attendance');
  }

  const data = (await response.json()) as CoacheePostAttendanceMarkResponseBody;
  return data;
}
